<template lang="pug">
.dashboard-wrapper
  b-row
    b-col
      b-card(no-body, class="pt-2 px-2")
        filters(
          :zoneFilterShow="zone_agency_filter"
          :agencyFilterShow="zone_agency_filter"
          :locationFilterShow="true",
          :observerFilterShow="true",
          :dateRangeFilterShow="true",
          :processTypeFilterShow="true",
          :processFilterShow="true",
          @changeFilter="updateFilter"
        )
  
  b-row(v-if="isLoading")
    b-col
      b-card(class="py-5")
        loading(
          :active="true" 
          :is-full-page="false"
          color="#498ceb"
        )

  div(v-else)
    b-row(v-if="!confirmations_total")
      b-col
        b-card(class="text-center")
          h5(class="mb-0")
            | {{ $t('message.no_confirmations_found') }}

    div(v-else)
      b-row
        b-col(sm="3")
          statistic-card-vertical-total(:statistic="confirmations_total")
      
      b-row
        b-col(cols="12")
          bar-chart-process(
            v-if="confirmationsPerDay.labels && confirmationsPerDay.labels.length",
            :process-data="confirmationsPerDay"
          )

      //- b-card
        b-table-simple(hover, small, caption-top, responsive)
          b-thead(head-variant="light")
            b-tr
              th {{ $t('message.tableHeader.date') }}
              th {{ $t('message.tableHeader.worker') }}
              th {{ $t('message.tableHeader.observer') }}
              th {{ $t('message.tableHeader.location') }}
              th {{ $t('message.tableHeader.process') }}
              th {{ $t('message.tableHeader.score') }}
              th {{ $t('message.tableHeader.total') }}
              th {{ $t('message.tableHeader.adherence') }}
              th {{ $t('message.tableHeader.action') }}
            b-tr(v-for="(confirmation, index) in confirmations", :key="index")
              b-td {{ confirmation.dateString }}
              b-td {{ confirmation.worker }}
              b-td {{ confirmation.observer }}
              b-td {{ confirmation.location }}
              b-td {{ confirmation.process }}
              b-td {{ confirmation.score }}
              b-td {{ confirmation.total }}
              b-td {{ confirmation.adherenceText() }}
              b-td
                view-button(@clicked="router.push({name: 'habit-confirmation-view', params: {id: confirmation.id}})")
                user-button(@clicked="router.push({name: 'apps-users-view', params: {id: confirmation.workerId}})")
      
      b-card
        b-table(
          :items="confirmations"
          :fields="tableHeaders"
          hover
          responsive
        )
          template(#cell(date)="data")
            span {{ data.item.dateString }}
          template(#cell(adherence)="data")
            span {{ data.item.adherenceText() }}
          template(#cell(actions)="data")
            div(class="flex flex-column")
              view-button(@clicked="router.push({name: 'habit-confirmation-view', params: {id: data.item.id}})")
              //- user-button(@clicked="router.push({name: 'apps-users-view', params: {id: data.item.workerId}})")

      
      b-button(v-if="hasPermission('confirmations-daily', 'export')", :disabled="!confirmations.length")
        json-excel(:data="confirmations", :fields="excelFields")
          | {{ $t('download_xlsx') }}
</template>

<script>
import { onMounted, ref, computed, watch } from "@vue/composition-api/dist/vue-composition-api";
import useNotifications from "@/composables/useNotifications";
import { BTable, BTableSimple, BThead, BTr, BTd, BCard } from "bootstrap-vue";
import JsonExcel from "vue-json-excel";
import StatisticCardVerticalTotal from './charts/StatisticCardVerticalTotal.vue'
import BarChartProcess from './charts/ChartjsBarChartProcess.vue'
import Filters from "@/views/organization/Filters.vue";
import i18n from '@/libs/i18n'
import ViewButton from '@/views/components/Shared/Buttons/ViewButton.vue'
import UserButton from '@/views/components/Shared/Buttons/UserButton.vue'
import { useRouter } from '@core/utils/utils'
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import store from "@/store";
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import { hasPermission, permissionScope } from '@/composables/usePermission'

export default {
  components: {
    BTable,
    BTableSimple,
    BThead,
    BTr,
    BTd,
    BCard,
    JsonExcel,
    StatisticCardVerticalTotal,
    Filters,
    ViewButton,
    UserButton,
    BarChartProcess,
    Loading,
  },
  setup() {
    const { showSuccessMessage, showErrorMessage } = useNotifications();
    const { handleError } = useCommon()
    const { getItemsAggregate } = useLambda()
    
    const userData = store.state?.userStore?.userData;
    const userObjectId = oid(userData.worker_id.$oid)
    const userRole = userData.role
    const clientId = userData.role !== "admin" ? userData.client.$oid : null;
    const userLocations = userData.locations?.length ? userData.locations : null;
    const { zone_agency_filter, default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const confirmations = ref([]);
    const confirmations_total = ref(0);
    const confirmationsPerDay = ref({})
    const locationFilter = ref([]);
    const observerFilter = ref([]);
    const zoneFilter = ref([]);
    const agencyFilter = ref([]);
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    const currentDay = now.getDate()
    const lastDayOfMonthDate = new Date(currentYear, currentMonth + 1, 0)
    const lastDayOfMonthNumber = lastDayOfMonthDate.getDate()
    const thirtyDaysBeforeDate = new Date(currentYear, currentMonth, currentDay - 30)
    const dateRangeFilter = default_language === "en"
      ? ref(`${thirtyDaysBeforeDate.getMonth() + 1}-${thirtyDaysBeforeDate.getDate()}-${thirtyDaysBeforeDate.getFullYear()} to ${currentMonth + 1}-${currentDay}-${currentYear}`)
      : ref(`${thirtyDaysBeforeDate.getDate()}-${thirtyDaysBeforeDate.getMonth() + 1}-${thirtyDaysBeforeDate.getFullYear()} to ${currentDay}-${currentMonth + 1}-${currentYear}`)
    const excelFields = ref({})
    const { router } = useRouter()
    const { processAdherence, getDatesFromRange } = useCommonDashboards()
    const isLoading = ref(true)
    const processTypeFilter = ref([])
    const processFilter = computed(() => store.state.filtersStore.processFilter)
    const companyFilter = computed(() => store.state.filtersStore.companyFilter)
    const idFilter = computed(() => store.state.filtersStore.idFilter)

    // Table Headers
    const tableHeaders = computed(() => {
      return [
        {
          key: 'date',
          label: i18n.t('message.tableHeader.date'),
          sortable: true,
        },
        {
          key: 'worker',
          label: i18n.t('message.tableHeader.worker'),
          sortable: true,
        },
        {
          key: 'observer',
          label: i18n.t('label.evaluator'),
          sortable: true,
        },
        {
          key: 'location',
          label: i18n.t('message.tableHeader.location'),
          sortable: true,
        },
        {
          key: 'process',
          label: i18n.t('message.tableHeader.process'),
          sortable: true,
        },
        {
          key: 'score',
          label: i18n.t('message.tableHeader.score'),
          sortable: true,
        },
        {
          key: 'total',
          label: i18n.t('message.tableHeader.total'),
          sortable: true,
        },
        {
          key: 'adherence',
          label: i18n.t('message.tableHeader.adherence'),
          sortable: true,
        },
        {
          key: 'actions',
          label: i18n.t('message.tableHeader.actions'),
        },
      ]
    })

    async function listConfirmations() {
      isLoading.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      try {
        const initialQuery = {
          pending: { $ne: true },
          date: { $gte: date(startFilter), $lt: date(endFilter) },
        }
        if (clientId) initialQuery.client_id = oid(clientId)

        if (processFilter.value.length > 0) {
          initialQuery.process = { $in: processFilter.value.map(id => oid(id)) }
        }

        const finalQuery = {}

        if (observerFilter.value.length > 0) {
          finalQuery["supervisor._id"] = { $in: observerFilter.value }
        }

        if (locationFilter.value.length > 0) {
          finalQuery["worker.locations._id"] = { $in: locationFilter.value }
        }

        const conditions = []

        // Process type filter
        if (processTypeFilter.value.length) {
          conditions.push({
            $or: processTypeFilter.value.map(codePrefix => ({
              'process.code': { $regex: `^${codePrefix}` }
            }))
          })
        }

        // Privileges filter
        if (permissionScope('confirmations-daily', 'view') === 'own') {
          if (userRole === 'employee') {
            conditions.push({
              $or: [
                { 'process.evaluator': userObjectId },
              ]
            })
          } else {
            conditions.push({
              $or: [
                { 'process.responsibles': userObjectId },
                { 'process.leadResponsibles': userObjectId },
                { 'process.evaluator': userObjectId },
              ]
            })
          }
        }

         if (companyFilter.value.length > 0) {
          conditions.push({ 'process.company': { $in: companyFilter.value.map(e => oid(e)) } })
        }
  
        if (idFilter.value.length > 0) {
          conditions.push({ 'process.id': { $in: idFilter.value.map(e => oid(e)) } })
        }

        // Armar el match final
        if (conditions.length) {
          finalQuery.$and = conditions
        }

        const pipeline = [
          { $match: initialQuery },
          { $lookup: { from: 'worker', localField: 'worker', foreignField: '_id', as: 'worker', pipeline: [ { $project: { deleted: 1, supervisors: 1, name: 1, locations: 1, roles: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'worker', localField: 'supervisors', foreignField: '_id', as: 'supervisors', pipeline: [ { $project: { name: 1, locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } },
          { $lookup: { from: 'worker', localField: 'supervisor', foreignField: '_id', as: 'supervisor', pipeline: [ { $project: { name: 1, locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } },
          { $lookup: { from: 'process', localField: 'process', foreignField: '_id', as: 'process', pipeline: [ { $project: { name: 1, code: 1, responsibles: 1, leadResponsibles: 1, evaluator: 1, company: 1, id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
          { $addFields: { worker: { $arrayElemAt: ["$worker", 0] }, supervisor: { $arrayElemAt: ["$supervisor", 0] }, process: { $arrayElemAt: ["$process", 0] } } },
          { $match: finalQuery },
          { $addFields: { _id: { $toString: "$_id" } } },
        ]

        const items = await getItemsAggregate({ collection: 'confirmation', pipeline })

        parseConfirmationData(items, startFilter, endFilter)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_confirmation_list') })
      } finally {
        isLoading.value = false
      }
    }

    // Data for adherence XLSX File
    function parseConfirmationData(items, startFilter, endFilter) {
      excelFields.value = {
        [i18n.t('message.tableHeader.date')]: "dateString",
        [i18n.t('message.tableHeader.worker')]: "worker",
        [i18n.t('message.tableHeader.observer')]: "observer",
        [i18n.t('message.tableHeader.location')]: "location",
        [i18n.t('message.tableHeader.process')]: "process",
        [i18n.t('message.tableHeader.score')]: "score",
        [i18n.t('message.tableHeader.total')]: "total",
        [i18n.t('message.tableHeader.adherence')]: "adherence",
        [i18n.t('message.tableHeader.duration')]: "duration",
      }

      let confirmationData = []
      
      // Set date labels for chart based on the date filter
      let dailyConfirmationData = {}
      let dynamicDate = startFilter
      // Change the dates so that it matches the UTC time zone
      const timezoneOffsetHours = dynamicDate.getTimezoneOffset() / 60
      dynamicDate.setHours(dynamicDate.getHours() + timezoneOffsetHours)
      endFilter.setHours(endFilter.getHours() + timezoneOffsetHours)
      while (dynamicDate < endFilter) {
        const dateToAdd = dynamicDate.toLocaleDateString(`${default_language || 'en'}-US`)
        dailyConfirmationData[dateToAdd] = 0
        dynamicDate = new Date(dynamicDate.getFullYear(), dynamicDate.getMonth(), dynamicDate.getDate() + 1)
      }

      for (const confirmation of items) {
        if (confirmation.total) {
          const date = new Date(confirmation.date)
          date.setHours(date.getHours() + timezoneOffsetHours)
          const dateString = date.toLocaleDateString(`${default_language || 'en'}-US`)
          const score = confirmation.score
          const total = confirmation.total
          let payload = {
            date,
            dateString,
            worker: confirmation.worker?.name,
            observer: confirmation.supervisor?.name,
            location: confirmation.worker?.locations?.map(e => e.location).join(" / "),
            process: confirmation.process?.name,
            score: Number(score) % 1 === 0 ? Number(score) : Number(score).toFixed(1),
            total: total,
            adherence: processAdherence(score, total),
            adherenceText: function() {
              if (this.total === 0) return "-"
              return `${this.adherence}%`
            },
            duration: confirmation.duration,
            id: confirmation._id,
            workerId: confirmation.worker?._id
          }

          // Add metadata fields if applicable
          if (confirmation.metadata) {
            confirmation.metadata.forEach(e => {
              payload[e.name] = e.answer
              if (!excelFields.value.hasOwnProperty(i18n.t(`metadata.${e.name}`))) {
                excelFields.value[i18n.t(`metadata.${e.name}`)] = e.name
              }
            })
          }
          
          confirmationData.push(payload);

          // Data for bar charts
          dailyConfirmationData[dateString]++
        }
      }

      confirmations.value = confirmationData
      confirmations_total.value = confirmationData.length
      excelFields.value[i18n.t('message.tableHeader.id')] = "id"

      let dailyConfirmationLabels = Object.keys(dailyConfirmationData)
      let dailyConfirmationValues = Object.values(dailyConfirmationData)

      confirmationsPerDay.value = {
        labels: dailyConfirmationLabels,
        data: dailyConfirmationValues
      };
    }

    function updateFilter(data) {
      locationFilter.value = data.locationFilter;
      observerFilter.value = data.observerFilter;
      dateRangeFilter.value = data.dateRangeFilter;
      zoneFilter.value = data.zoneFilter;
      agencyFilter.value = data.agencyFilter;
      processTypeFilter.value = data.processTypeFilter
      // processFilter.value = data.processFilter // processFilter ya se actualiza automáticamente desde el store global

      listConfirmations();
    }

    watch([companyFilter, idFilter], () => {
      listConfirmations()
    })

    onMounted(() => {
      listConfirmations()
    })

    return {
      hasPermission,
      updateFilter,
      confirmations,
      confirmations_total,
      confirmationsPerDay,
      excelFields,
      router,
      isLoading,
      zone_agency_filter,
      tableHeaders,
    };
  },
};
</script>
